import { ArrowUpIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMe } from '@/api/hooks/useMe'
import { Button } from '@/components/ui/button'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'
import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'

export const QuickChatInput = () => {
  const dispatch = useAppDispatch()
  const { selectedIndividual } = useIndividuals()
  const { data: me } = useMe()
  const { canUseChat, isTrial } = useSubscription()
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // "Ask a question about <name>…" reads strangely when that name is the asker's own —
  // drop the name in that one case rather than in general.
  const isSelf = selectedIndividual?.users.find(u => u.id === me?.id)?.relation === 'self'

  // Why the field is disabled comes first; what to type in it is only useful
  // once it can be typed in. A trial is not read-only — it simply does not
  // include the chat — so saying "requires an active subscription" would
  // misdescribe it.
  const placeholder = !canUseChat
    ? t(isTrial ? 'subscription.chatTrialTooltip' : 'subscription.readOnlyTooltip')
    : isSelf
      ? t('quickChatInput.placeholder')
      : t('quickChatInput.placeholderAbout', { name: selectedIndividual?.firstName ?? '' })

  // rows={1} fixes the box to one line, so a placeholder that wraps on a narrow screen
  // (e.g. the disabled/trial message) gets clipped instead of pushing the box taller.
  // Growing it off scrollHeight — same approach as the main chat input — fixes that, and
  // re-runs on resize since wrapping depends on the container's width, not just the text.
  const fixHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  useEffect(() => {
    fixHeight()
  }, [text, placeholder])

  useEffect(() => {
    window.addEventListener('resize', fixHeight)

    return () => window.removeEventListener('resize', fixHeight)
  }, [])

  const handleSubmit = () => {
    if (!text.trim()) return
    dispatch(AssistantActions.setActiveThreadId(undefined))
    dispatch(AssistantActions.setPendingMessage(text.trim()))
    dispatch(AssistantActions.toggleSideChat(true))
    setText('')
  }

  return (
    <div className="card p-2">
      <form
        className="flex items-end gap-2"
        onSubmit={e => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <textarea
          className="w-full flex-1 resize-none bg-transparent px-2 py-2 text-base outline-none placeholder:text-text-secondary"
          disabled={!canUseChat}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSubmit()
            }
          }}
          placeholder={placeholder}
          ref={textareaRef}
          rows={1}
          value={text}
        />
        <Button
          className="shrink-0"
          disabled={!canUseChat || !text.trim()}
          size="icon"
          type="submit"
          variant="secondary"
        >
          <ArrowUpIcon />
        </Button>
      </form>
    </div>
  )
}

export default QuickChatInput
